import React, { useEffect, useMemo, useRef, useState } from 'react';
import StableMuiDialog from '../../../../../common/StableMuiDialog.js';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import logger from '../../../../../utils/logger';
import { toast } from '../../../../../common/toast';
import { usePersistedDataStore } from '../../../../store.js';
import { findRunWorkupFiles, findScarFilingFile } from '../../../Workups/WorkupVisualization/workupFileHelpers.js';
import { WORKUP_REPORT_TYPES, getWorkupReportLabel } from '../../../../../common/mappings.js';
import { displayFileLabel, getNyscefRawFilename, groupNyscefExhibits, nyscefLastModifiedTime, uniqueNyscefDownloadFilename } from '../../../NyscefUpload/nyscefExhibitUtils.js';
import PrinterPicker, { usePrinters } from './PrinterPicker.js';
import PrintQueuePanel from './PrintQueuePanel.js';
import { clearFinished, startBatch, uploadBatch, usePrintQueue } from './printQueue.js';

const WORKUP_OPTIONS = WORKUP_REPORT_TYPES.filter((r) => !r.hideFromUi && r.id !== 'loa');
const ROW_GRID = 'grid grid-cols-[32px_minmax(0,1fr)_minmax(0,1.2fr)_104px] items-center gap-3 px-5';
const SPLIT_GRID = 'grid grid-cols-[32px_minmax(0,1fr)_minmax(0,1.2fr)_auto] items-center gap-3 px-5';
const QTY_SLOT = 'inline-flex w-20 justify-center';

// ---------- Toggle styling (sx so it beats MUI's own styles) ----------
const C = {
  ink: '#17212B',
  sub: '#4A5465',
  control: '#C9D1DC',
  blue: '#1F4E9C',
  tint: '#E3EBF8',
};

// Header source picker: grey tray, selected item lifts to white.
const TAB_SX = {
  gap: '2px',
  p: '3px',
  bgcolor: '#EEF1F6',
  borderRadius: '9px',
  '& .MuiToggleButtonGroup-grouped, & .MuiToggleButtonGroup-grouped:not(:first-of-type), & .MuiToggleButtonGroup-grouped:not(:last-of-type)': {
    m: 0,
    border: 0,
    borderRadius: '7px',
  },
  '& .MuiToggleButton-root': {
    height: 30,
    px: 1.5,
    py: 0,
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 550,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    textTransform: 'none',
    color: C.sub,
    '&:hover': { bgcolor: 'rgba(255,255,255,.6)' },
  },
  '& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover': {
    bgcolor: '#fff',
    color: C.ink,
    boxShadow: '0 1px 2px rgba(16,24,40,.12), 0 0 0 1px rgba(16,24,40,.06)',
  },
};

// Workup / custom source pickers: bordered segmented control, blue tint when selected.
const SEGMENT_SX = {
  '& .MuiToggleButton-root': {
    height: 30,
    px: 1.5,
    py: 0,
    fontFamily: 'inherit',
    fontSize: 13,
    fontWeight: 500,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    textTransform: 'none',
    color: '#3A4556',
    bgcolor: '#fff',
    borderColor: C.control,
    '&:hover': { bgcolor: '#F3F6FA' },
  },
  '& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover': {
    bgcolor: C.tint,
    color: C.blue,
    fontWeight: 650,
  },
  '& .MuiToggleButton-root.Mui-disabled': { color: '#A3ACB8' },
  '& .MuiToggleButtonGroup-firstButton, & .MuiToggleButtonGroup-grouped:first-of-type': {
    borderTopLeftRadius: '8px',
    borderBottomLeftRadius: '8px',
  },
  '& .MuiToggleButtonGroup-lastButton, & .MuiToggleButtonGroup-grouped:last-of-type': {
    borderTopRightRadius: '8px',
    borderBottomRightRadius: '8px',
  },
};

// Compact switch: 30x18 pill, thumb sits inside the track, blue when on.
const SWITCH_SX = {
  width: 30,
  height: 18,
  p: 0,
  '& .MuiSwitch-switchBase': {
    p: '2px',
    color: '#fff',
    '&:hover, &.Mui-checked:hover': { bgcolor: 'transparent' },
    '&.Mui-checked': { transform: 'translateX(12px)', color: '#fff' },
    '&.Mui-checked + .MuiSwitch-track': { bgcolor: C.blue, opacity: 1 },
    '&.Mui-disabled + .MuiSwitch-track': { opacity: 0.5 },
  },
  '& .MuiSwitch-thumb': { width: 14, height: 14, boxShadow: '0 1px 2px rgba(16,24,40,.3)' },
  '& .MuiSwitch-track': { borderRadius: '9px', bgcolor: '#B8C1CE', opacity: 1 },
};

const CHECKBOX_SX = {
  p: '2px',
  color: '#8A94A6',
  '&:hover, &.Mui-checked:hover': { bgcolor: 'transparent' },
  '&.Mui-checked': { color: C.blue },
  '& .MuiSvgIcon-root': { fontSize: 18 },
};

const naturalCmp = (a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

const parseName = (name) => {
  const base = name.replace(/\.pdf$/i, '');
  const m = base.match(/^(\d+)-(\d{4})\s+(.*)$/);
  return m ? { caseNo: `${m[1]}-${m[2]}`, label: m[3] } : { caseNo: '', label: base };
};

const clamp = (n) => Math.min(99, Math.max(0, Number.isFinite(n) ? n : 0));

const readDroppedFiles = async (fileList) => {
  const out = [];
  let JSZip = null;
  for (const f of Array.from(fileList || [])) {
    if (/\.zip$/i.test(f.name)) {
      if (!JSZip) {
        const jszipModule = await import('jszip');
        JSZip = jszipModule.default ?? jszipModule;
      }
      const zip = await JSZip.loadAsync(f);
      for (const entry of Object.values(zip.files)) {
        const base = entry.name.split('/').pop();
        if (entry.dir || entry.name.startsWith('__MACOSX') || base.startsWith('.') || !/\.pdf$/i.test(base)) continue;
        out.push({ name: base, bytes: await entry.async('uint8array') });
      }
    } else if (/\.pdf$/i.test(f.name) || f.type === 'application/pdf') {
      out.push({ name: f.name, bytes: new Uint8Array(await f.arrayBuffer()) });
    }
  }
  return out;
};

const getPageCount = async (bytes) => {
  const { PDFDocument } = await import('pdf-lib');
  const doc = await PDFDocument.load(bytes, { ignoreEncryption: true });
  return doc.getPageCount();
};

// jobs: [{ bytes, copies }] in print order. Output is file 1 x copies, then file 2 x copies.
const buildCombinedPdf = async (jobs) => {
  const { PDFDocument } = await import('pdf-lib');
  const out = await PDFDocument.create();
  for (const job of jobs) {
    if (job.copies < 1) continue;
    const src = await PDFDocument.load(job.bytes, { ignoreEncryption: true });
    const idx = src.getPageIndices();
    const pad = idx.length % 2 === 1;
    const { width, height } = src.getPage(idx.length - 1).getSize();
    for (let c = 0; c < job.copies; c++) {
      const pages = await out.copyPages(src, idx);
      pages.forEach((p) => out.addPage(p));
      if (pad) out.addPage([width, height]);
    }
  }
  return out.save();
};

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const buildIndividualZip = async (jobs) => {
  const jszipModule = await import('jszip');
  const JSZip = jszipModule.default ?? jszipModule;
  const zip = new JSZip();
  const used = new Set();
  for (const job of jobs) {
    zip.file(uniqueNyscefDownloadFilename(job.sourceName || job.name, used), job.bytes);
  }
  return zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 3 } });
};

const estUploadDay = (ms) => {
  if (ms == null) return null;
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(ms);
};

const inUploadRange = (ms, from, to) => {
  if (!from && !to) return true;
  const day = estUploadDay(ms);
  if (!day) return false;
  const start = from && to ? (from < to ? from : to) : (from || to);
  const end = from && to ? (from < to ? to : from) : (from || to);
  return day >= start && day <= end;
};

const printPdfName = (parts) => {
  const base = parts.filter(Boolean).join(' ').replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim();
  return `${(base || 'document').slice(0, 240)}.pdf`;
};

const pdfCache = new Map();

const pdfCacheId = (parcelId, docKind, file) => {
  const name = file?.Key || file?.Filename || file?.filename || '';
  return `${parcelId}:${docKind}:${String(name).split('?')[0]}:${file?.LastModified || ''}`;
};

const rememberPdf = (doc) => {
  const existing = pdfCache.get(doc.cacheId);
  if (existing?.promise) return existing.promise;
  if (existing?.bytes || existing?.error) return Promise.resolve(existing);
  const promise = (async () => {
    try {
      const response = await fetch(doc.url, { mode: 'cors' });
      if (!response.ok) throw new Error(`Couldn't fetch ${doc.label}`);
      const bytes = new Uint8Array(await response.arrayBuffer());
      const saved = { pages: await getPageCount(bytes), bytes };
      pdfCache.set(doc.cacheId, saved);
      return saved;
    } catch (e) {
      const saved = { error: e.message || 'Could not read this PDF' };
      pdfCache.set(doc.cacheId, saved);
      return saved;
    }
  })();
  pdfCache.set(doc.cacheId, { pending: true, promise });
  return promise;
};

const uploadedLabel = (ms) => {
  if (ms == null) return null;
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(ms).map((part) => [part.type, part.value]));
  return `${p.month}/${p.day}/${p.year} ${p.hour}:${p.minute}${(p.dayPeriod || '').toLowerCase()}`;
};

const exhibitDoc = (row, rowIndex, section, entry) => {
  const parcelId = row.parcel_id || row.id || '';
  const address = row.Address || '';
  const caseNo = row.SCARIndexNumber || '';
  const label = entry.fullLabel || displayFileLabel(entry.file, entry.listIndex);
  const uploadedMs = nyscefLastModifiedTime(entry.file);
  return {
    id: `${rowIndex}:${section}:${entry.listIndex}`,
    cacheId: pdfCacheId(parcelId, section, entry.file),
    url: entry.file.Url,
    label,
    name: printPdfName([caseNo, address, label]),
    sourceName: getNyscefRawFilename(entry.file),
    uploadedAt: uploadedLabel(uploadedMs),
    uploadedMs,
    address,
    caseNo,
    parcelId,
    section,
  };
};

const collectEvidence = (rows, sections) => (rows || []).flatMap((row, rowIndex) => {
  if (!row || row.loading) return [];
  const groups = groupNyscefExhibits(row.NyscefS3Files);
  return sections.flatMap((section) => groups[section].map((entry) => exhibitDoc(row, rowIndex, section, entry)));
});

const groupByCase = (list) => {
  const groups = [];
  list.forEach((it) => {
    const key = it.parcelId || (it.caseNo ? `c:${it.caseNo}` : `file:${it.id}`);
    let group = groups[groups.length - 1];
    if (!group || group.key !== key) {
      group = { key, address: it.address || '', caseNo: it.caseNo || '', files: [], flags: [] };
      groups.push(group);
    }
    if (!group.address && it.address) group.address = it.address;
    group.files.push(it);
  });
  return groups;
};

const listAventineGroups = (rows, workupType, includeFiling) => {
  const groups = [];
  let caseCount = 0;
  let filingCount = 0;
  (rows || []).forEach((row, rowIndex) => {
    if (!row || row.loading || row.SCARFiled !== 1) return;
    caseCount += 1;
    const files = Array.isArray(row.S3Files) ? row.S3Files : [];
    const filing = findScarFilingFile(files);
    const parcelId = row.parcel_id || row.id || '';
    const address = row.Address || '';
    const caseNo = row.SCARIndexNumber || '';
    const docs = [];
    const flags = [];
    const add = (docKind, label, file) => docs.push({
      id: `${rowIndex}:${docKind}:${label}`,
      cacheId: pdfCacheId(parcelId, docKind, file),
      url: file.Url,
      label,
      name: printPdfName([caseNo, address, label]),
      kind: docKind,
    });
    if (workupType !== 'none') {
      const workups = findRunWorkupFiles(files, workupType);
      if (!workups.length) flags.push(`No ${getWorkupReportLabel(workupType)}`);
      workups.forEach((w) => add('workup', w.title, w.file));
    }
    if (filing) filingCount += 1;
    else flags.push('No SCAR filing');
    if (filing && includeFiling) add('filing', 'SCAR Filing', filing);
    if (docs.length || flags.length) groups.push({ key: parcelId || `row:${rowIndex}`, address, caseNo, files: docs, flags });
  });
  return { groups, caseCount, filingCount };
};

let uid = 0;

export default function EvidencePrintDialog({
  open,
  onClose,
  rows = [],
  selectedRows = [],
  onClearSelection,
  evidenceFileName = 'evidence',
}) {
  const reportType = usePersistedDataStore((s) => s.negotiationObj?.reportType);
  const isAssessment = String(reportType || 'standard').toLowerCase() === 'assessment';
  const defaultWorkupType = isAssessment ? 'onepagermaps' : 'fnmaSummary';
  const [tab, setTab] = useState('town');
  const [customKind, setCustomKind] = useState('files');
  const [items, setItems] = useState([]);
  const [copies, setCopies] = useState(1);
  const [filingCopies, setFilingCopies] = useState(1);
  const [perCase, setPerCase] = useState(false);
  const [caseCopies, setCaseCopies] = useState({});
  const [caseFilingCopies, setCaseFilingCopies] = useState({});
  const [includeOther, setIncludeOther] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [workupType, setWorkupType] = useState(defaultWorkupType);
  const [includeFiling, setIncludeFiling] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [downloadAnchor, setDownloadAnchor] = useState(null);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [dropOver, setDropOver] = useState(false);
  const [pdfMeta, setPdfMeta] = useState({});
  const pickerRef = useRef(null);
  const printerPicker = usePrinters(open);
  const { printer } = printerPicker;
  const printerName = printer?.name || 'default printer';
  const printQueue = usePrintQueue(open);
  const usingSelection = selectedRows.length > 0;
  const printRows = useMemo(() => {
    if (!usingSelection) return rows;
    const order = new Map((rows || []).map((row, index) => [row.id, index]));
    return [...selectedRows].sort(
      (a, b) => (order.get(a.id) ?? Number.MAX_SAFE_INTEGER) - (order.get(b.id) ?? Number.MAX_SAFE_INTEGER),
    );
  }, [rows, selectedRows, usingSelection]);
  const townItems = useMemo(
    () => collectEvidence(printRows, includeOther ? ['town', 'other'] : ['town']).filter((it) => inUploadRange(it.uploadedMs, dateFrom, dateTo)),
    [printRows, includeOther, dateFrom, dateTo],
  );
  const otherCount = useMemo(
    () => collectEvidence(printRows, ['other']).filter((it) => inUploadRange(it.uploadedMs, dateFrom, dateTo)).length,
    [printRows, dateFrom, dateTo],
  );
  const uploadedItems = useMemo(
    () => collectEvidence(printRows, ['aventine']).filter((it) => inUploadRange(it.uploadedMs, dateFrom, dateTo)),
    [printRows, dateFrom, dateTo],
  );
  const townGroups = useMemo(() => groupByCase(townItems), [townItems]);
  const uploadedGroups = useMemo(() => groupByCase(uploadedItems), [uploadedItems]);
  const aventine = useMemo(() => listAventineGroups(printRows, workupType, includeFiling), [printRows, workupType, includeFiling]);
  const customGroups = useMemo(() => groupByCase(items), [items]);
  const customFiles = tab === 'custom' && customKind === 'files';
  const groups = { town: townGroups, uploaded: uploadedGroups, custom: customKind === 'aventine' ? aventine.groups : customGroups }[tab];
  const shownGroups = useMemo(() => groups.map((g) => ({
    ...g,
    files: g.files.map((f) => {
      const hit = f.cacheId && (pdfMeta[f.cacheId] || pdfCache.get(f.cacheId));
      return hit ? { ...f, pages: hit.pages, error: hit.error } : f;
    }),
  })), [groups, pdfMeta]);
  const pageDocs = useMemo(() => (open ? groups.flatMap((g) => g.files).filter((f) => f.cacheId) : []), [open, groups]);

  useEffect(() => {
    if (open) return;
    setTab('town');
    setCustomKind('files');
    setItems([]);
    setCopies(1);
    setFilingCopies(1);
    setPerCase(false);
    setCaseCopies({});
    setCaseFilingCopies({});
    setIncludeOther(true);
    setDateFrom('');
    setDateTo('');
    setWorkupType(defaultWorkupType);
    setIncludeFiling(false);
    setConfirmOpen(false);
    setDownloadAnchor(null);
    setStatus('');
    setBusy(false);
    setDropOver(false);
    clearFinished({ onlyClean: true }); // keep finished prints that had problems on show
  }, [open, defaultWorkupType]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (const doc of pageDocs) {
        const saved = await rememberPdf(doc);
        if (cancelled) return;
        setPdfMeta((prev) => (prev[doc.cacheId] === saved ? prev : { ...prev, [doc.cacheId]: saved }));
      }
    })();
    return () => { cancelled = true; };
  }, [pageDocs]);

  const splitQty = tab === 'custom' && customKind === 'aventine' && includeFiling && workupType !== 'none';
  const filingOnly = tab === 'custom' && customKind === 'aventine' && includeFiling && workupType === 'none';
  const rowGrid = splitQty ? SPLIT_GRID : ROW_GRID;
  const copiesFor = (g, file) => {
    const filing = file?.kind === 'filing';
    const chosen = perCase
      ? ((filing ? caseFilingCopies[g.key] : caseCopies[g.key]) ?? (filing ? filingCopies : copies))
      : (filing ? filingCopies : copies);
    return Number(chosen) || 0;
  };
  const printableGroups = shownGroups.filter((g) => g.files.some((f) => !f.error && copiesFor(g, f) > 0));
  const queued = printableGroups.flatMap((g) => g.files.filter((f) => !f.error && copiesFor(g, f) > 0).map((f) => ({ ...f, copies: copiesFor(g, f) })));
  const stapledCount = queued.reduce((sum, f) => sum + f.copies, 0);
  const pagesLoading = queued.some((f) => !Number.isFinite(f.pages));
  const totalPages = queued.reduce((sum, f) => sum + (Number.isFinite(f.pages) ? f.copies * (f.pages + (f.pages % 2)) : 0), 0);
  const tabCounts = { town: townItems.length, uploaded: uploadedItems.length, custom: items.length };

  const addFiles = async (fileList) => {
    if (!fileList?.length) return;
    setStatus('Reading files…');
    try {
      const incoming = await readDroppedFiles(fileList);
      const newItems = incoming.map((f) => ({ id: ++uid, ...f, sourceName: f.name, ...parseName(f.name) }));
      setItems((prev) => [...prev, ...newItems].sort((a, b) => naturalCmp(a.name, b.name)));
      for (const item of newItems) {
        let pages;
        let error = '';
        try {
          pages = await getPageCount(item.bytes);
        } catch (_e) {
          error = 'Could not read this PDF';
        }
        setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, pages, error } : it)));
      }
      setStatus(incoming.length ? `Added ${incoming.length} file${incoming.length > 1 ? 's' : ''}.` : 'No PDFs found in that drop.');
    } catch (e) {
      logger.error(e);
      setStatus(`Couldn't read those files: ${e.message}`);
    }
  };

  const finishJobs = async (jobs, action, format) => {
    if (action === 'print') {
      // The server owns the queue: it prints strictly in this order, one document at a time, checks each
      // one actually printed, and pauses + emails if anything doesn't. It keeps going if this window closes.
      setStatus(`Starting the print on the ${printerName}…`);
      const batchId = await startBatch({ printerId: printer?.id, jobs });
      const failed = await uploadBatch(batchId, jobs.length, (i) => setStatus(`Uploading ${i + 1} of ${jobs.length}; printing has started…`));
      setStatus(failed
        ? `${failed} of ${jobs.length} files didn't upload. The print will pause at the first one so nothing prints out of order.`
        : `All ${jobs.length} file${jobs.length === 1 ? ' is' : 's are'} on the print server. They'll print in order even if you close this window.`);
      return;
    }
    const base = evidenceFileName || 'evidence';
    if (format === 'individual') {
      setStatus('Building zip…');
      triggerDownload(await buildIndividualZip(jobs), `${base}.zip`);
      setStatus('Downloaded zip.');
      return;
    }
    setStatus('Building PDF…');
    const bytes = await buildCombinedPdf(jobs);
    triggerDownload(new Blob([bytes], { type: 'application/pdf' }), `${base}.pdf`);
    setStatus('Downloaded PDF.');
  };

  const run = async (action, format) => {
    setDownloadAnchor(null);
    setBusy(action);
    try {
      const jobs = [];
      for (let i = 0; i < queued.length; i++) {
        const it = queued[i];
        setStatus(`Preparing ${i + 1} of ${queued.length}…`);
        const saved = it.bytes ? it : await rememberPdf(it);
        if (!saved.bytes) throw new Error(saved.error || `Couldn't fetch ${it.label}`);
        jobs.push({ bytes: saved.bytes, copies: it.copies, name: it.name, sourceName: it.sourceName, pages: saved.pages });
      }
      await finishJobs(jobs, action, format);
    } catch (e) {
      logger.error(e);
      const message = e?.response?.data?.error || e.message || 'Print failed';
      setStatus(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  // Blue on the active tab, grey on the others.
  const badge = (n, active) => (
    <span className={`ml-1.5 rounded-full px-1.5 text-[11px] font-semibold leading-[18px] ${active ? 'bg-[#E3EBF8] text-[#1F4E9C]' : 'bg-[#DFE4EC] text-[#4A5465]'}`}>
      {n}
    </span>
  );

  const stepper = (value, onChange, label) => (
    <span className="inline-flex items-center rounded-md border border-[#D5DCE3] bg-white">
      <IconButton size="small" aria-label={`Fewer ${label}`} disabled={busy} onClick={() => onChange(clamp((Number(value) || 0) - 1))} sx={{ width: 22, minWidth: 22, height: 28, p: 0, borderRadius: 0 }}>−</IconButton>
      <input
        type="number"
        size={2}
        className="w-6 min-w-6 max-w-6 border-0 bg-transparent p-0 text-center text-[15px] font-semibold text-[#17212B] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        min={0}
        max={99}
        value={value}
        disabled={busy}
        aria-label={label}
        onChange={(e) => onChange(e.target.value === '' ? '' : clamp(parseInt(e.target.value, 10)))}
        onBlur={(e) => onChange(clamp(parseInt(e.target.value, 10)))}
      />
      <IconButton size="small" aria-label={`More ${label}`} disabled={busy} onClick={() => onChange(clamp((Number(value) || 0) + 1))} sx={{ width: 22, minWidth: 22, height: 28, p: 0, borderRadius: 0 }}>+</IconButton>
    </span>
  );

  const qtyReadout = (value, onChange, label) => (perCase
    ? stepper(value, onChange, label)
    : <span className="text-[13px] font-semibold text-[#5C6B7A]">×{Number(value) || 0}</span>);

  const chip = (f) => {
    const pagesKnown = Number.isFinite(f.pages);
    const tip = f.error || [
      pagesKnown ? `${f.pages} page${f.pages === 1 ? '' : 's'}${f.pages % 2 ? ' (+1 blank)' : ''}` : 'Counting pages…',
      f.section === 'other' && 'Other document',
    ].filter(Boolean).join(' · ');
    const tone = f.error ? 'border-[#E7B4B4] bg-[#FDECEC] text-[#9B1C1C]' : 'border-[#C9D6EA] bg-white text-[#1F4E9C]';
    const cls = `inline-flex max-w-full items-center gap-1 rounded-md border px-2 py-0.5 text-[13px] ${tone}`;
    const body = (
      <>
        <span className="truncate font-semibold">{f.label}</span>
        {pagesKnown ? <span className="whitespace-nowrap text-[#5C6B7A]">{f.pages} pp</span> : !f.error && <CircularProgress size={10} sx={{ color: '#5C6B7A' }} />}
      </>
    );
    return (
      <span key={f.id} className="inline-flex max-w-full items-center gap-1.5">
        <Tooltip title={tip} disableInteractive>
          {f.url ? (
            <a className={`${cls} hover:bg-[#E3EBF8]`} href={f.url} target="_blank" rel="noopener noreferrer">
              {body}
              <OpenInNewIcon sx={{ fontSize: 12, opacity: 0.7 }} aria-hidden="true" />
            </a>
          ) : (
            <span className={cls}>
              {body}
              <IconButton size="small" aria-label={`Remove ${f.label}`} disabled={busy} onClick={() => setItems((p) => p.filter((x) => x.id !== f.id))} sx={{ p: 0, fontSize: 12, color: '#5C6B7A' }}>✕</IconButton>
            </span>
          )}
        </Tooltip>
        {f.uploadedAt && <span className="whitespace-nowrap text-xs text-[#5C6B7A]">{f.uploadedAt}</span>}
      </span>
    );
  };

  const dropProps = customFiles ? {
    onDragOver: (e) => { e.preventDefault(); setDropOver(true); },
    onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDropOver(false); },
    onDrop: (e) => { e.preventDefault(); setDropOver(false); addFiles(e.dataTransfer.files); },
  } : {};

  const eachCopies = filingOnly ? filingCopies : copies;
  const summary = queued.length
    ? `${printableGroups.length} case${printableGroups.length === 1 ? '' : 's'}, ${queued.length} document${queued.length === 1 ? '' : 's'}, ${
      perCase ? 'copies set per case'
        : splitQty ? `${Number(copies) || 0} evidence ${Number(copies) === 1 ? 'copy' : 'copies'} and ${Number(filingCopies) || 0} SCAR filing ${Number(filingCopies) === 1 ? 'copy' : 'copies'}`
          : `${Number(eachCopies) || 0} cop${Number(eachCopies) === 1 ? 'y' : 'ies'} each`}`
    : 'Nothing to print';

  return (
    <StableMuiDialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="evidence-print-title"
      PaperProps={{ sx: { position: 'relative', height: '85vh', maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
        '& button[disabled]': { bgcolor: '#EEF1F6 !important', color: '#A3ACB8 !important' },
        '& [role=status] button[disabled]': { bgcolor: 'transparent !important' },
      } }}
    >
      <div className="flex min-h-0 flex-1 flex-col text-[15px] leading-normal text-[#17212B] [font-variant-numeric:tabular-nums]">
        <div className="flex shrink-0 items-center gap-4 border-b border-[#D5DCE3] px-5 py-3">
          <h1 id="evidence-print-title" className="text-xl font-bold">Print</h1>
          {printer?.simulated && <span className="rounded-md bg-[#FFF4E0] px-2 py-0.5 text-xs font-semibold text-[#7A4A00]">Test mode: nothing prints</span>}
          <ToggleButtonGroup exclusive value={tab} disabled={busy} aria-label="What to print" sx={TAB_SX} onChange={(e, v) => { if (v) { setTab(v); setStatus(''); } }}>
            <ToggleButton value="town">Town Evidence{badge(tabCounts.town, tab === 'town')}</ToggleButton>
            <ToggleButton value="uploaded">Uploaded Evidence{badge(tabCounts.uploaded, tab === 'uploaded')}</ToggleButton>
            <ToggleButton value="custom">Custom{tabCounts.custom > 0 && badge(tabCounts.custom, tab === 'custom')}</ToggleButton>
          </ToggleButtonGroup>
          <IconButton aria-label="Close" onClick={onClose} disabled={busy} sx={{ ml: 'auto', color: '#5C6B7A' }}>
            <CloseIcon />
          </IconButton>
        </div>

        <div className="flex shrink-0 flex-col gap-2 border-b border-[#D5DCE3] px-5 py-2.5 text-sm">
          <div className="flex items-center gap-3 whitespace-nowrap">
            {tab === 'town' && (
              <label className="inline-flex cursor-pointer items-center gap-2 bg-transparent p-0">
                <Switch disableRipple checked={includeOther} disabled={busy} onChange={(e) => setIncludeOther(e.target.checked)} sx={SWITCH_SX} />
                Other documents
                <span className="text-[#5C6B7A]">{otherCount}</span>
              </label>
            )}
            {tab !== 'custom' && (
              <span className="inline-flex items-center gap-1.5">
                Uploaded
                <input type="date" aria-label="Uploaded from" className="rounded-md border border-[#D5DCE3] px-1 py-0.5 text-[13px]" value={dateFrom} disabled={busy} max={dateTo || undefined} onChange={(e) => setDateFrom(e.target.value)} />
                <span className="text-[#5C6B7A]">to</span>
                <input type="date" aria-label="Uploaded to" className="rounded-md border border-[#D5DCE3] px-1 py-0.5 text-[13px]" value={dateTo} disabled={busy} min={dateFrom || undefined} onChange={(e) => setDateTo(e.target.value)} />
                {(dateFrom || dateTo) && (
                  <Button size="small" disabled={busy} onClick={() => { setDateFrom(''); setDateTo(''); }} className="min-w-0 font-semibold normal-case text-[#1F4E9C]">Clear</Button>
                )}
              </span>
            )}
            {tab === 'custom' && (
              <ToggleButtonGroup exclusive value={customKind} disabled={busy} aria-label="Custom source" sx={SEGMENT_SX} onChange={(e, v) => { if (v) { setCustomKind(v); setStatus(''); } }}>
                <ToggleButton value="files">Custom Documents</ToggleButton>
                <ToggleButton value="aventine">Aventine Evidence</ToggleButton>
              </ToggleButtonGroup>
            )}
            {tab === 'custom' && customKind === 'aventine' && (
              <span className="inline-flex items-center gap-2">
                Workup
                <ToggleButtonGroup exclusive value={workupType} disabled={busy} aria-label="Workup" sx={SEGMENT_SX} onChange={(e, v) => v && setWorkupType(v)}>
                  <ToggleButton value="none">None</ToggleButton>
                  {WORKUP_OPTIONS.map((r) => (
                    <ToggleButton key={r.id} value={r.id} disabled={isAssessment && r.id === 'fnmaSummary'}>{r.label}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </span>
            )}
            {customFiles && items.length > 0 && (
              <span className="inline-flex gap-2">
                <Button variant="outlined" size="small" disabled={busy} onClick={() => pickerRef.current?.click()} className="rounded-md border-[#D5DCE3] font-semibold normal-case text-[#17212B]">Add files</Button>
                <Button variant="outlined" size="small" disabled={busy} onClick={() => { setItems([]); setStatus(''); }} className="rounded-md border-[#D5DCE3] font-semibold normal-case text-[#17212B]">Clear</Button>
              </span>
            )}
            <span className="ml-auto inline-flex items-center gap-3">
              {splitQty ? (
                <span className={`inline-flex items-center gap-3 transition-opacity ${perCase ? 'opacity-50' : ''}`}>
                  <span className="inline-flex items-center gap-2">Evidence{stepper(copies, setCopies, 'evidence copies')}</span>
                  <span className="inline-flex items-center gap-2">Filing{stepper(filingCopies, setFilingCopies, 'filing copies')}</span>
                </span>
              ) : (
                <span className={`inline-flex items-center gap-2 transition-opacity ${perCase ? 'opacity-50' : ''}`}>
                  {filingOnly ? 'Filing' : 'Copies'}
                  {stepper(filingOnly ? filingCopies : copies, filingOnly ? setFilingCopies : setCopies, filingOnly ? 'filing copies' : 'copies')}
                </span>
              )}
              {!(tab === 'custom' && customKind === 'aventine') && (
                <label className="inline-flex cursor-pointer items-center gap-1.5 bg-transparent p-0 text-[#1F4E9C]">
                  <Checkbox disableRipple checked={perCase} disabled={busy} onChange={(e) => setPerCase(e.target.checked)} sx={CHECKBOX_SX} />
                  Per case
                </label>
              )}
            </span>
          </div>
          {tab === 'custom' && customKind === 'aventine' && (
            <div className="flex items-center justify-end gap-4">
              <label className="inline-flex cursor-pointer items-center gap-1.5 bg-transparent p-0 text-[#1F4E9C]">
                <Checkbox disableRipple checked={perCase} disabled={busy} onChange={(e) => setPerCase(e.target.checked)} sx={CHECKBOX_SX} />
                Per case
              </label>
              <label className="inline-flex w-fit cursor-pointer items-center gap-2 bg-transparent p-0">
                <Switch disableRipple checked={includeFiling} disabled={busy} onChange={(e) => {
                  const on = e.target.checked;
                  if (on) {
                    setFilingCopies(copies);
                    setCaseFilingCopies({ ...caseCopies });
                  }
                  setIncludeFiling(on);
                }} sx={SWITCH_SX} />
                Include SCAR Filing
                <span className="text-[#5C6B7A]">{aventine.filingCount} of {aventine.caseCount}</span>
              </label>
            </div>
          )}
        </div>

        <input ref={pickerRef} type="file" multiple accept=".pdf,.zip,application/pdf,application/zip" hidden onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />

        <div className={`${rowGrid} shrink-0 border-b border-[#D5DCE3] bg-[#F7F9FB] py-1.5 text-xs font-medium text-[#5C6B7A]`}>
          <span>#</span>
          <Tooltip title="Sort the table to reorder the print." disableInteractive>
            <span className="w-fit">Case, in table order</span>
          </Tooltip>
          <span>Documents</span>
          <span className={splitQty ? 'flex justify-end gap-2' : 'text-right'}>
            {splitQty ? <><span className={QTY_SLOT}>Evidence</span><span className={QTY_SLOT}>Filing</span></> : 'Qty'}
          </span>
        </div>

        <div className={`min-h-0 flex-1 overflow-auto ${customFiles && dropOver ? 'outline outline-2 -outline-offset-2 outline-[#1F4E9C]' : ''}`} {...dropProps}>
          {customFiles && !items.length ? (
            <div
              className={`m-5 flex h-[calc(100%-2.5rem)] cursor-pointer flex-col items-center justify-center rounded-[10px] border-2 border-dashed p-7 text-center ${dropOver ? 'border-[#1F4E9C] bg-[#E3EBF8]' : 'border-[#D5DCE3]'}`}
              role="button"
              tabIndex={0}
              onClick={() => pickerRef.current?.click()}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && pickerRef.current?.click()}
            >
              <strong className="block text-[17px]">Drop PDFs or a .zip here</strong>
              <span className="text-sm text-[#5C6B7A]">or click to choose files. Files group by case number.</span>
            </div>
          ) : !shownGroups.length ? (
            <p className="px-5 py-4 text-[#5C6B7A]">
              {tab !== 'custom' && (dateFrom || dateTo)
                ? `No documents uploaded ${dateFrom && dateTo && dateFrom !== dateTo ? 'in that range' : 'on that date'}.`
                : `No documents in the ${usingSelection ? 'selected cases' : 'cases currently shown'}.`}
            </p>
          ) : shownGroups.map((g, i) => {
            const caseLabel = g.address || g.caseNo || 'this case';
            const hasEvidence = g.files.some((f) => f.kind !== 'filing' && !f.error);
            const hasFiling = g.files.some((f) => f.kind === 'filing' && !f.error);
            return (
              <div key={`${g.key}:${i}`} className={`${rowGrid} border-b border-[#EEF1F4] py-2`}>
                <span className="text-[#5C6B7A]">{i + 1}</span>
                <span className="flex min-w-0 flex-wrap items-baseline gap-x-2">
                  <strong className="truncate">{g.address || g.caseNo || 'No case number'}</strong>
                  {g.address && g.caseNo && <span className="text-[13px] text-[#5C6B7A]">SCAR {g.caseNo}</span>}
                  {g.flags.map((flag) => <span key={flag} className="text-[13px] text-[#A35F00]">{flag}</span>)}
                </span>
                <span className="flex min-w-0 flex-wrap gap-1.5">{g.files.map(chip)}</span>
                <span className={splitQty ? 'flex justify-end gap-2 justify-self-end' : 'justify-self-end'}>
                  {splitQty ? (
                    <>
                      <span className={QTY_SLOT}>
                        {hasEvidence && qtyReadout(perCase ? (caseCopies[g.key] ?? copies) : copies, (v) => setCaseCopies((p) => ({ ...p, [g.key]: v })), `evidence copies of ${caseLabel}`)}
                      </span>
                      <span className={QTY_SLOT}>
                        {hasFiling && qtyReadout(perCase ? (caseFilingCopies[g.key] ?? filingCopies) : filingCopies, (v) => setCaseFilingCopies((p) => ({ ...p, [g.key]: v })), `SCAR filing copies of ${caseLabel}`)}
                      </span>
                    </>
                  ) : g.files.some((f) => !f.error) && qtyReadout(
                    perCase
                      ? ((filingOnly ? caseFilingCopies[g.key] : caseCopies[g.key]) ?? (filingOnly ? filingCopies : copies))
                      : (filingOnly ? filingCopies : copies),
                    (v) => (filingOnly
                      ? setCaseFilingCopies((p) => ({ ...p, [g.key]: v }))
                      : setCaseCopies((p) => ({ ...p, [g.key]: v }))),
                    `copies of ${caseLabel}`,
                  )}
                </span>
              </div>
            );
          })}
        </div>

        {(busy === 'print' || printQueue.jobs.length > 0) && <PrintQueuePanel queue={printQueue} />}

        <div className="flex shrink-0 flex-wrap items-center gap-3 border-t border-[#D5DCE3] px-5 py-3">
          <div className="min-w-0 flex-1 text-sm text-[#5C6B7A]" role="status" aria-live="polite">
            {status ? <span className="text-[#1F4E9C]">{status}</span> : (
              <span className="inline-flex items-center gap-1">
                {summary}
                {queued.length > 0 && <>, {pagesLoading ? <CircularProgress size={11} sx={{ color: '#5C6B7A' }} /> : totalPages} pages</>}
              </span>
            )}
            {usingSelection && (
              <span className="block">
                From the {selectedRows.length} selected case{selectedRows.length === 1 ? '' : 's'}.{' '}
                <button type="button" disabled={busy} onClick={onClearSelection} className="font-semibold text-[#1F4E9C] hover:underline">Use all cases</button>
              </span>
            )}
          </div>
          <PrinterPicker {...printerPicker} disabled={busy} />
          <Button variant="outlined" disabled={busy} onClick={onClose} className="rounded-md border-[#D5DCE3] font-semibold normal-case text-[#17212B]">Cancel</Button>
          <Button variant="outlined" disabled={busy || !queued.length} loading={busy === 'open'} loadingPosition="start" endIcon={<ArrowDropDownIcon />} aria-haspopup="menu" aria-expanded={Boolean(downloadAnchor)} onClick={(e) => setDownloadAnchor(e.currentTarget)} className="rounded-md border-[#1F4E9C] font-semibold normal-case text-[#1F4E9C]">
            Download
          </Button>
          <Button variant="contained" disabled={busy || !queued.length} loading={busy === 'print'} loadingPosition="start" onClick={() => setConfirmOpen(true)} className="rounded-md bg-[#1F4E9C] px-5 font-semibold normal-case hover:bg-[#1a4284]">
            Print {stapledCount || ''}
          </Button>
        </div>

        <Menu
          anchorEl={downloadAnchor}
          open={Boolean(downloadAnchor)}
          onClose={() => setDownloadAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          slotProps={{ paper: { sx: { minWidth: 220 } } }}
        >
          <MenuItem onClick={() => run('open', 'combined')}>Combined PDF</MenuItem>
          <MenuItem onClick={() => run('open', 'individual')}>Individual PDFs (zipped)</MenuItem>
        </Menu>
        {confirmOpen && (
          <div className="absolute inset-0 z-[3] flex items-center justify-center bg-[rgba(23,33,43,.35)] p-6">
            <div className="w-full max-w-[440px] rounded-xl bg-white px-[22px] pb-4 pt-5 text-[#17212B] shadow-[0_12px_40px_rgba(23,33,43,.18)]" role="dialog" aria-modal="true" aria-labelledby="print-confirm-title">
              <h2 id="print-confirm-title" className="mb-2 text-lg font-bold">Print to the {printerName}?</h2>
              <p className="mb-4 text-sm text-[#5C6B7A]">Each document is its own stapled job, and every copy of one document finishes before the next starts.</p>
              {(['warning', 'error', 'offline'].includes(printer?.state) || printer?.staples === false) && (
                <p className="mb-4 rounded-md bg-[#FFF4E0] px-3 py-2 text-sm text-[#7A4A00]">
                  Heads up: {printer.staples === false ? "this printer reports it can't staple, so it will likely refuse the job" : printer.detail}.
                </p>
              )}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Button variant="outlined" disabled={busy} onClick={() => setConfirmOpen(false)} className="rounded-md border-[#D5DCE3] bg-white font-semibold normal-case text-[#17212B] hover:border-[#5C6B7A] hover:bg-white">Cancel</Button>
                <Button variant="contained" disabled={busy} onClick={() => { setConfirmOpen(false); run('print'); }} className="rounded-md bg-[#1F4E9C] px-5 py-2.5 font-semibold normal-case hover:bg-[#1a4284]">Print</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </StableMuiDialog>
  );
}
